import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db, collection, getDocs, query, where, fmt, fmtDate, statusBadge } from "../config/firebase";
import "./Orders.css";

const Orders = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "orders"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const items = [];
      snap.forEach((docSnap) => {
        items.push({ docId: docSnap.id, ...docSnap.data() });
      });
      items.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });
      setOrders(items);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
      return;
    }
    if (user) {
      fetchOrders();
    }
  }, [user, authLoading, navigate, fetchOrders]);

  if (authLoading) {
    return (
      <div className="orders-page">
        <div className="orders-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-container">
        <h1 className="orders-title">My Orders</h1>

        {loading ? (
          <div className="orders-loading">
            <div className="spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="orders-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            <h3>No orders yet</h3>
            <p>Start shopping and your orders will appear here.</p>
            <button className="btn-shop-now" onClick={() => navigate("/")}>Shop Now</button>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => {
              const badge = statusBadge(order.status);
              return (
                <div key={order.docId} className="order-card">
                  <div className="order-card-header">
                    <div className="order-meta">
                      <span className="order-number">Order #{order.id}</span>
                      <span className={`order-status ${badge.class}`}>{badge.label}</span>
                    </div>
                    <span className="order-date">
                      {order.createdAt?.toDate
                        ? fmtDate(order.createdAt.toDate().toISOString())
                        : order.createdAt
                        ? fmtDate(typeof order.createdAt === "string" ? order.createdAt : new Date(order.createdAt.seconds * 1000).toISOString())
                        : "N/A"}
                    </span>
                  </div>

                  <div className="order-items">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="order-item-row">
                        <div className="order-item-info">
                          <span className="order-item-name">{item.productName}</span>
                          {item.variantLabel && (
                            <span className="order-item-variant">{item.variantLabel}</span>
                          )}
                        </div>
                        <div className="order-item-qty">× {item.quantity}</div>
                        <div className="order-item-price">{fmt(item.price * item.quantity)}</div>
                      </div>
                    ))}
                  </div>

                  <div className="order-card-footer">
                    <div className="order-totals">
                      <div className="total-row">
                        <span>Subtotal</span>
                        <span>{fmt(order.subtotal || 0)}</span>
                      </div>
                      <div className="total-row">
                        <span>Advance Paid</span>
                        <span>{fmt(order.advance || 0)}</span>
                      </div>
                      <div className="total-row grand">
                        <span>Total</span>
                        <span>{fmt(order.total || 0)}</span>
                      </div>
                    </div>
                    {order.paymentId && (
                      <div className="payment-info">
                        Payment ID: <span>{order.paymentId}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;